import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import logger from "../utils/logger";

class NotificationWebSocketService {
  constructor() {
    this.client = null;
    this.connected = false;
    this.subscription = null;
    this.callbacks = new Map(); // callbackId -> callback
    this.missedNotificationCallbacks = new Map(); // callbackId -> callback for missed notifications
    this.callbackIdCounter = 0;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.shouldReconnect = true;
    this.baseUrl = null;
  }

  /**
   * Get fresh auth token from localStorage
   * This ensures reconnection uses the latest token (fixes stale token issue)
   */
  getAuthToken() {
    return localStorage.getItem("access_token") || localStorage.getItem("authToken");
  }

  /**
   * Get current user from localStorage
   */
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem("currentUser");
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      logger.error("Failed to parse current user:", error);
      return null;
    }
  }

  /**
   * Fetch notifications from REST API with pagination
   * @param {number} page - Page number (1-indexed)
   * @param {number} size - Number of items per page
   * @returns {Object} { notifications, pagination }
   */
  async fetchNotifications(page = 1, size = 15) {
    const user = this.getCurrentUser();
    const token = this.getAuthToken();

    if (!user?.id || !token || !this.baseUrl) {
      logger.log("Cannot fetch notifications: missing user, token, or baseUrl");
      return { notifications: [], pagination: { hasNext: false, page: 1, totalPages: 0 } };
    }

    try {
      const apiUrl = `${this.baseUrl.replace(/\/$/, "")}/messaging/api/v1/push-notifications/user?userId=${user.id}&page=${page}&size=${size}`;
      logger.log("📬 Fetching notifications from:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Handle various response formats
      let notifications = [];
      if (Array.isArray(data)) {
        // Direct array of notifications
        notifications = data;
      } else if (data.privateNotifications || data.publicNotifications || data.roleBasedNotifications) {
        // Nested format with notification types
        const privateNotifs = data.privateNotifications || [];
        const publicNotifs = data.publicNotifications || [];
        const roleBasedNotifs = data.roleBasedNotifications || [];
        notifications = [...privateNotifs, ...publicNotifs, ...roleBasedNotifs];
      } else {
        // Wrapped response (data.data or data.content)
        notifications = data.data || data.content || [];
      }

      // Extract pagination metadata
      const pagination = {
        page: data.page || page,
        size: data.size || size,
        totalElements: data.totalElements || notifications.length,
        totalPages: data.totalPages || 1,
        hasNext: data.hasNext || false,
        hasPrevious: data.hasPrevious || false,
      };

      const unreadCount = notifications.filter(n => n.readStatus === false).length;
      logger.log(
        `📬 Fetched ${notifications.length} notifications (${unreadCount} unread), page ${pagination.page}/${pagination.totalPages}`
      );

      return { notifications, pagination };
    } catch (error) {
      logger.error("Failed to fetch notifications:", error);
      return { notifications: [], pagination: { hasNext: false, page: 1, totalPages: 0 } };
    }
  }

  /**
   * Fetch missed notifications from REST API (first page)
   * Called on connect/reconnect to catch notifications missed while offline
   * @returns {Object} { notifications, pagination }
   */
  async fetchMissedNotifications() {
    return this.fetchNotifications(1, 15);
  }

  /**
   * Notify callbacks about missed notifications
   * @param {Object} result - { notifications, pagination }
   */
  notifyMissedNotifications(result) {
    const { notifications, pagination } = result;
    if (!notifications || notifications.length === 0) return;

    logger.log(`📬 Notifying callbacks about ${notifications.length} notifications`);

    // Notify missed notification callbacks with both notifications and pagination
    this.missedNotificationCallbacks.forEach(callback => {
      try {
        callback({ notifications, pagination });
      } catch (err) {
        logger.error("Error in missed notification callback:", err);
      }
    });

    // Also notify regular callbacks for each notification (for backwards compatibility)
    notifications.forEach(notification => {
      this.callbacks.forEach(callback => {
        try {
          callback(notification);
        } catch (err) {
          logger.error("Error in notification callback:", err);
        }
      });
    });
  }

  /**
   * Handle post-connection setup: fetch missed notifications and subscribe
   */
  async onConnected() {
    this.connected = true;
    this.reconnectAttempts = 0;
    logger.log("✅ Notification WebSocket connected");

    // Resubscribe to WebSocket
    this.resubscribe();

    // Fetch and notify about missed notifications
    if (this.callbacks.size > 0 || this.missedNotificationCallbacks.size > 0) {
      const result = await this.fetchMissedNotifications();
      this.notifyMissedNotifications(result);
    }
  }

  connect(baseUrl, _authToken) {
    // Note: _authToken parameter kept for backwards compatibility but we always use fresh token
    if (this.connected) {
      logger.log("Notification WebSocket already connected");
      return Promise.resolve();
    }

    const authToken = this.getAuthToken();
    if (!authToken) {
      logger.error("Cannot connect: No auth token available");
      return Promise.reject(new Error("No auth token"));
    }

    this.baseUrl = baseUrl;
    this.shouldReconnect = true;
    return new Promise((resolve, reject) => {
      try {
        const httpUrl = baseUrl.replace(/\/$/, "") + "/messaging/ws/notifications";
        logger.log("Connecting to notification WebSocket:", httpUrl);

        this.client = new Client({
          webSocketFactory: () => new SockJS(httpUrl),
          connectHeaders: {
            Authorization: `Bearer ${authToken}`,
          },
          reconnectDelay: 0,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,
          debug: str => {
            logger.log("Notification WS Debug:", str);
          },
          onConnect: () => {
            this.onConnected().then(() => resolve());
          },
          onStompError: frame => {
            logger.error("❌ Notification WebSocket STOMP error:", frame);
            logger.error("Error details:", frame.headers, frame.body);
            this.connected = false;
            reject(new Error("Notification WebSocket connection failed"));
          },
          onWebSocketError: error => {
            logger.error("❌ Notification WebSocket error:", error);
            this.connected = false;
          },
          onWebSocketClose: () => {
            this.connected = false;
            logger.log("Notification WebSocket closed");
            if (this.shouldReconnect) {
              this.handleReconnect();
            }
          },
        });

        this.client.activate();
      } catch (error) {
        logger.error("Failed to initialize notification WebSocket:", error);
        reject(error);
      }
    });
  }

  handleReconnect() {
    if (!this.shouldReconnect || !this.baseUrl) {
      logger.log("Reconnection disabled or no baseUrl, stopping");
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.log("⚠️ Max notification reconnection attempts reached - stopping permanently");
      this.shouldReconnect = false;
      return;
    }

    this.reconnectAttempts++;
    logger.log(`Reconnecting notifications... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
    setTimeout(() => {
      if (this.shouldReconnect) {
        // Connect will get fresh token from localStorage
        this.connect(this.baseUrl).catch(() => {
          logger.error("Notification reconnection failed");
        });
      }
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  /**
   * Resubscribe after reconnection
   */
  resubscribe() {
    if (!this.client || !this.connected) return;

    // Clear old subscription reference (invalid after reconnect)
    this.subscription = null;

    // If there are callbacks, recreate the STOMP subscription
    if (this.callbacks.size > 0) {
      this.createStompSubscription();
    }
  }

  /**
   * Create STOMP subscription if not exists
   */
  createStompSubscription() {
    if (!this.client || !this.connected) {
      logger.error("Cannot subscribe: Notification WebSocket not connected");
      return null;
    }

    // If subscription already exists, return it
    if (this.subscription) {
      return this.subscription;
    }

    try {
      this.subscription = this.client.subscribe("/user/queue/notifications", message => {
        try {
          const notification = JSON.parse(message.body);
          logger.log("🔔 New notification:", notification);
          // Notify all callbacks
          this.callbacks.forEach(callback => {
            try {
              callback(notification);
            } catch (err) {
              logger.error("Error in notification callback:", err);
            }
          });
        } catch (error) {
          logger.error("Failed to parse notification:", error);
        }
      });

      logger.log("Subscribed to /user/queue/notifications");
      return this.subscription;
    } catch (error) {
      logger.error("Failed to subscribe to notifications:", error);
      return null;
    }
  }

  /**
   * Subscribe to notifications with a callback
   * @param {Function} callback - Called for each new notification
   * @param {Function} onMissedNotifications - Optional callback for missed notifications (receives array)
   * Returns an object with unsubscribe method for cleanup
   */
  subscribeToNotifications(callback, onMissedNotifications = null) {
    // Generate unique callback ID
    const callbackId = ++this.callbackIdCounter;
    this.callbacks.set(callbackId, callback);
    logger.log(`Added notification callback ${callbackId}`);

    // Register missed notifications callback if provided
    if (onMissedNotifications) {
      this.missedNotificationCallbacks.set(callbackId, onMissedNotifications);
      logger.log(`Added missed notification callback ${callbackId}`);
    }

    // Create STOMP subscription if connected and not already subscribed
    if (this.connected) {
      this.createStompSubscription();

      // If we're already connected when subscribing, fetch missed notifications
      this.fetchMissedNotifications().then(result => {
        const { notifications, pagination } = result;
        if (notifications.length > 0) {
          if (onMissedNotifications) {
            try {
              onMissedNotifications({ notifications, pagination });
            } catch (err) {
              logger.error("Error in missed notification callback:", err);
            }
          }
          // Also send each to the regular callback
          notifications.forEach(notification => {
            try {
              callback(notification);
            } catch (err) {
              logger.error("Error in notification callback:", err);
            }
          });
        }
      });
    }

    // Return an object with unsubscribe method for this specific callback
    return {
      id: callbackId,
      unsubscribe: () => this.unsubscribeCallback(callbackId),
    };
  }

  /**
   * Update an existing callback
   */
  updateCallback(callbackId, newCallback) {
    if (this.callbacks.has(callbackId)) {
      this.callbacks.set(callbackId, newCallback);
      logger.log(`Updated notification callback ${callbackId}`);
      return true;
    }
    return false;
  }

  /**
   * Unsubscribe a specific callback
   */
  unsubscribeCallback(callbackId) {
    this.callbacks.delete(callbackId);
    this.missedNotificationCallbacks.delete(callbackId);
    logger.log(`Removed notification callback ${callbackId}`);

    // If no more callbacks, unsubscribe from STOMP
    if (this.callbacks.size === 0) {
      this.unsubscribe();
    }
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
      logger.log("Unsubscribed from notifications");
    }
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.client) {
      this.unsubscribe();
      this.callbacks.clear();
      this.missedNotificationCallbacks.clear();
      this.client.deactivate();
      this.connected = false;
      logger.log("Notification WebSocket disconnected");
    }
  }

  isConnected() {
    return this.connected;
  }
}

export default new NotificationWebSocketService();
